import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, FormControl, RadioGroup, FormControlLabel, Radio, TextField, Typography, Tooltip } from '@mui/material';

import { fetchDefaultFeedbackMessages, submitFeedback } from '../../../modules/reports/thunks';
import Modal from '../../../ui/Modal';
import Select from '../../../ui/Select';
import { ahPath } from '../../../utilities/UrlPaths';
import { FEEDBACK_TYPE, FEEDBACK_SELECT_TYPE, FEEDBACK_TYPE_MENU } from './constants';

const FeedbackModal = ({ bidderName, isOpen, onClose, currentFeedbackLotRef, clickType, setIsButtonClickType, setLoadingFeedbackModalData, loadingFeedbackModalData, setRandomNumber, checkboxArray }) => {
  const dispatch = useDispatch();
  const messageRef = useRef('');

  const { selectedLotsForDisputes } = useSelector(state => state.reports);
  const defaultFeedbackMessages = useSelector(state => state.reports.defaultFeedbackMessages);
  const isSubmittingFeedback = useSelector(state => state.reports.isSubmittingFeedback);
  const isDefaultMessagesLoading = useSelector(state => state.reports.isDefaultMessagesLoading);
  const lotFeedback = useSelector(state => state.reports.feedbackMessage[currentFeedbackLotRef]);
  const { houseRef } = useSelector(state => state.house);

  const defaultSelectedOption = '1563';
  const currentLotfeedbackType = lotFeedback ? lotFeedback?.positive ? FEEDBACK_TYPE.POSITIVE
    : FEEDBACK_TYPE.NEGATIVE : FEEDBACK_TYPE.POSITIVE;
  const defaultFeedbackChecked = !!lotFeedback?.defaultFeedback;
  const customFeedbackChecked = !(lotFeedback?.defaultFeedback ?? true);
  const selectedDefaultFeedback = lotFeedback?.defaultFeedback
    ? lotFeedback.feedbackId
    : defaultSelectedOption;

  const [defaultFeedbackID, setDefaultFeedbackID] = useState(selectedDefaultFeedback);
  const [messageType, setMessageType] = useState(currentLotfeedbackType);
  const [isDefaultFeedbackSelectDisabled, setIsDefaultFeedbackSelectDisabled] = useState(false);
  const [error, setError] = useState('');
  const [feedbackSelectType, setFeedbackSelectType] = useState(FEEDBACK_SELECT_TYPE.DEFAULT);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const getMenus = (data) => {
    const menuItems = data?.map(({ message, feedbackID }) => ({
      value: feedbackID.toString(),
      label: message,
    }));

    return menuItems;
  };

  const defaultMessageMenu = useMemo(() => {
    if (defaultFeedbackMessages && defaultFeedbackMessages.length > 0) {
      return getMenus(defaultFeedbackMessages);
    }
    return [];
  }, [defaultFeedbackMessages]);

  const handleChange = (event) => {
    setFeedbackSelectType(event.target.value);
  };

  const handleChangeDefaultFeedback = ({ value, label }) => {
    setDefaultFeedbackID(value);
    setFeedbackMessage(label);
  };

  const handleChangeMessageType = ({ value }) => {
    setMessageType(value);
  };

  const handleSubmitFeedback = () => {
    let feedbackType;
    let feedback;
    let payload;
    if (feedbackSelectType === 'default') {
      feedbackType = messageType;
      feedback = feedbackMessage;
    } else {
      feedback = messageRef.current.value;
      if (!feedback) {
        return setError('Message is required!');
      }
      setError('');
      feedbackType = messageType;
    }

    switch (clickType) {
      case 'button':
        payload = {
          lotRefs: checkboxArray,
          feedbackType,
          feedback,
          bin: false
        };
        setIsButtonClickType('');
        break;
      case '':
        payload = {
          lotRefs: [currentFeedbackLotRef],
          feedbackType,
          feedback,
          bin: false
        };
        break;
      default:
        break;
    }

    dispatch(submitFeedback(houseRef, payload));
    setRandomNumber(Math.random());
    onClose();
  };

  useEffect(() => {
    if (houseRef && !defaultFeedbackMessages && !loadingFeedbackModalData) {
      dispatch(fetchDefaultFeedbackMessages(houseRef));
    }
  }, [houseRef, defaultFeedbackMessages]);

  useEffect(() => {
    const [firstMenu = {}] = defaultMessageMenu || [];
    setFeedbackMessage(firstMenu?.label);
  }, [defaultMessageMenu]);

  useEffect(() => {
    const allMessagesEmpty = defaultFeedbackMessages?.every(item => !item.message);
    if (allMessagesEmpty) setIsDefaultFeedbackSelectDisabled(true);
  }, [defaultFeedbackMessages]);

  useEffect(() => {
    if (defaultFeedbackChecked) {
      setFeedbackSelectType(FEEDBACK_SELECT_TYPE.DEFAULT);
    } else if (customFeedbackChecked) {
      setFeedbackSelectType(FEEDBACK_SELECT_TYPE.CUSTOM);
    }
  }, [lotFeedback]);

  const renderDefaultFeedbackSection = () => {
    const allMessagesEmpty = defaultFeedbackMessages?.every(item => !item.message);
    if (isDefaultMessagesLoading) {
      return (
        <ReactLoading className="loading-spinner" height="auto" width={30} type="spin" color="black" />
      );
    }

    if (allMessagesEmpty) {
      return (
        <>
          <Typography variant="body2" mt={1}>
            Save time by adding default positive feedback messages.
          </Typography>
          <Button
            variant="text"
            color="secondary"
            size="sm"
            className="mx-2"
            onClick={() => window.open(`${ahPath()}/ah/customers/feedback/default`, '_blank')}
          >
            Setup default feedback messages
          </Button>
        </>
      );
    }

    return (
      <Select
        sx={{ width: 440 }}
        size="small"
        defaultValue={selectedDefaultFeedback}
        onChange={handleChangeDefaultFeedback}
        menus={defaultMessageMenu}
      />
    );
  };

  if (loadingFeedbackModalData) {
    return (
      <Modal isOpen={isOpen} toggle={onClose}>
        <Modal.Body>
          <Box>
            <ReactLoading className="loading-spinner" height="auto" width={60} type="spokes" color="black" />
          </Box>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <Modal.Header toggle={onClose}>
        {clickType === 'button' ? 'Bulk Feedback' : 'Feedback'}
      </Modal.Header>
      <Modal.Body>
        <Box>
          <p>
            {clickType === 'button'
              ? 'Leave Feedback For Selected Lots'
              : `Leave Feedback For ${bidderName}`}
          </p>
        </Box>
        <Box mb={2}>
          <Button
            variant="text"
            color="secondary"
            size="sm"
            onClick={() => window.open(`${ahPath()}/ar/customers/feedback/default`, '_blank')}
          >
            View default feedback messages
          </Button>
          <Tooltip
            title="Save time by creating three default feedback messages. These are reusable messages for positive feedback. You can setup these messages at any time and use them after all your auctions."
            style={{ backgroundColor: 'whiteSmoke' }}
            placement="bottom"
          >
            <span className="ml-2">
              <FontAwesomeIcon className="color-blue" id="questionCircle" icon="question-circle" />
            </span>
          </Tooltip>
        </Box>
        <FormControl>
          <RadioGroup
            aria-labelledby="controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={feedbackSelectType}
            onChange={handleChange}
          >
            <Box mb={4}>
              <FormControlLabel
                value="default"
                control={<Radio size="small" disabled={isDefaultFeedbackSelectDisabled} />}
                label="Use Stored Feedback"
              />
              <Box>
                {renderDefaultFeedbackSection()}
              </Box>
            </Box>
            <Box mb={2}>
              <FormControlLabel value="custom" control={<Radio size="small" />} label="Use Custom Feedback" />
              <Select
                sx={{ width: 200, mb: 2, display: 'block' }}
                size="small"
                defaultValue={messageType}
                onChange={handleChangeMessageType}
                menus={FEEDBACK_TYPE_MENU}
                disabled={feedbackSelectType === 'default'}
              />
              <TextField
                sx={{ width: 440 }}
                placeholder="Please write your message here."
                inputRef={messageRef}
                className="textarea"
                id="outlined-textarea"
                multiline
                rows={5}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="standard"
                disabled={feedbackSelectType === 'default'}
                defaultValue={(!lotFeedback?.defaultFeedback && lotFeedback?.message) || ''}
              />
              <Typography variant="body2" color="red" mt={1}>
                {error}
              </Typography>
            </Box>
          </RadioGroup>
        </FormControl>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="text" color="secondary" className="mr-2" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmitFeedback} color="primary" variant="contained" disabled={isSubmittingFeedback}>
          <Box display="flex" alignItems="center" gap={2}>
            Submit Feedback
            {isSubmittingFeedback && (
              <ReactLoading className="ml-2" type="spin" height="auto" width={15} />
            )}
          </Box>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FeedbackModal;
